import { useEffect } from "react";
import L from "leaflet";
import { useMap } from "react-leaflet";
import proj4 from "proj4";
import { toGridRef } from "../../utils/osGridRef";

proj4.defs(
  "EPSG:27700",
  "+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 " +
    "+x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +units=m +no_defs"
);

// Determine number of digits for label based on zoom
const getDigitsFromZoom = (zoom) => {
  if (zoom >= 13) return 10; // 1 meter precision
  if (zoom >= 11) return 8; // 10 m
  if (zoom >= 9) return 6; // 100 m
  return 4; // 1 km
};

export const OSGridOverlay = () => {
  const map = useMap();

  useEffect(() => {
    let gridLayer;

    // OS National Grid bounds (meters, EPSG:27700)
    const OSGB = { eMin: 0, eMax: 700000, nMin: 0, nMax: 1300000 };
    const withinOSGB = (e, n) =>
      Number.isFinite(e) &&
      Number.isFinite(n) &&
      e >= OSGB.eMin &&
      e <= OSGB.eMax &&
      n >= OSGB.nMin &&
      n <= OSGB.nMax;

    const safeToGridRef = (e, n, digits) => {
      try {
        return toGridRef(e, n, digits) || null;
      } catch {
        return null;
      }
    };

    const createGridLayer = () => {
      const zoom = map.getZoom();
      const digits = getDigitsFromZoom(zoom);

      if (gridLayer) gridLayer.remove();

      const GridLayer = L.GridLayer.extend({
        createTile(coords) {
          const tile = L.DomUtil.create("div", "os-grid-tile");
          tile.style.outline = "1px solid rgba(0,0,0,0.2)";
          tile.style.fontSize = "10px";
          tile.style.textAlign = "center";
          tile.style.lineHeight = "256px";

          const tileSize = 256;

          // Use tile CENTER to reduce edge cases at tile boundaries
          const centerPt = map.unproject(
            [(coords.x + 0.5) * tileSize, (coords.y + 0.5) * tileSize],
            coords.z
          );

          const [e, n] = proj4("EPSG:4326", "EPSG:27700", [
            centerPt.lng,
            centerPt.lat,
          ]);

          // Outside OSGB? Don’t label this tile.
          if (!withinOSGB(e, n)) return tile;

          const label = safeToGridRef(Math.round(e), Math.round(n), digits);

          // If mapping failed for some reason, leave it blank rather than crashing.
          tile.innerHTML = label ? `<div>${label}</div>` : "";
          return tile;
        },
      });

      gridLayer = new GridLayer();
      gridLayer.addTo(map);
    };

    createGridLayer();
    map.on("zoomend", createGridLayer);

    return () => {
      if (gridLayer) gridLayer.remove();
      map.off("zoomend", createGridLayer);
    };
  }, [map]);

  return null;
};
